/**
 * 콘텐츠의 처리 상태 — 영상 변환(2026-10-05)을 사람이 읽는 배지로.
 *
 * 서버는 화면이 못 트는 영상(MOV·ProRes·HEVC·4K·H.264 High)을 모든 화면이 푸는 재생본으로 바꾼다. 그동안 그 콘텐츠는
 * 편성에 있어도 나가지 않는다(처음 올린 것) — 목록에서 "왜 안 나오나" 를 바로 읽게 진행을 보인다. 이미 나가던 영상을
 * 다시 변환할 때는 원본이 계속 나가므로 경고가 아니라 안내로 보인다.
 */
import { useRef } from 'react';
import type { Asset } from './api/client.js';
import { usePolling } from './hooks.js';

export interface StatusBadge {
  label: string;
  tone: 'warn' | 'danger' | 'info';
  /** 마우스를 올리면 보이는 까닭. */
  title: string;
}

export function assetStatusBadge(asset: Pick<Asset, 'status' | 'transcode' | 'status_reason'>): StatusBadge | null {
  const job = asset.transcode ?? null;
  const reasons = job?.reasons?.length ? `변환하는 까닭: ${job.reasons.join(', ')}` : '';
  if (job && (job.state === 'queued' || job.state === 'running')) {
    const pct = Math.round((job.progress ?? 0) * 100);
    const label = job.state === 'queued' ? '변환 대기' : `변환 중 ${pct}%`;
    if (asset.status === 'ready') {
      return { label: `다시 ${label}`, tone: 'info', title: `다 될 때까지 원본이 나갑니다. ${reasons}`.trim() };
    }
    return { label, tone: 'warn', title: `변환이 끝나야 화면에 나갑니다. ${reasons}`.trim() };
  }
  if (asset.status === 'failed') {
    return {
      label: job?.state === 'failed' ? '변환 실패' : '처리 실패',
      tone: 'danger',
      title: asset.status_reason || job?.error || '처리하지 못했습니다',
    };
  }
  if (asset.status === 'processing') return { label: '처리 중', tone: 'warn', title: '처리가 끝나야 화면에 나갑니다' };
  return null;
}

/** 목록을 다시 불러와야 하는가 — 변환이 진행 중인 것이 있으면(진행률이 움직인다). */
export function transcodeInFlight(assets: ReadonlyArray<Pick<Asset, 'transcode'>>): boolean {
  return assets.some((asset) => asset.transcode?.state === 'queued' || asset.transcode?.state === 'running');
}

/**
 * 진행이 이만큼 그대로면 더 묻지 않는다.
 *
 * 서버가 변환 도중 내려가면 작업이 `running` 에 멈춘 채 남는다. 그것 하나 때문에 콘텐츠 탭을 열어 둔
 * 사람마다 5초에 한 번씩 목록 전부를 끝없이 다시 받았다. 변환은 길어도 진행률이 몇 분 안에는 움직이므로
 * 10분 동안 아무것도 바뀌지 않았으면 멈춘 것으로 본다 — 새로고침하거나 거르개를 바꾸면 다시 본다.
 */
export const TRANSCODE_STALL_MS = 10 * 60_000;

/**
 * 진행 중인 변환의 지금 모양 — 무엇이, 어느 단계, 몇 % 인가. 진행 중인 것이 없으면 빈 문자열.
 *
 * 이 값이 바뀌지 않으면 다시 물어도 같은 답이다. 순서는 받은 순서 그대로 — 같은 목록을 다시
 * 받으면 같은 순서다.
 */
export function transcodeSignature(assets: ReadonlyArray<Pick<Asset, 'id' | 'transcode'>>): string {
  return assets
    .filter((asset) => asset.transcode?.state === 'queued' || asset.transcode?.state === 'running')
    .map((asset) => `${asset.id}:${asset.transcode?.state}:${Math.round((asset.transcode?.progress ?? 0) * 100)}`)
    .join('|');
}

/** 다시 물을 것인가 — 진행 중인 변환이 있고, 마지막으로 움직인 지 `TRANSCODE_STALL_MS` 가 안 됐을 때. */
export function shouldPollTranscode(signature: string, changedAt: number, now: number): boolean {
  return signature !== '' && now - changedAt < TRANSCODE_STALL_MS;
}

/**
 * 변환이 진행 중인 동안 목록을 다시 부른다.
 *
 * `usePolling` 위에 얹는다 — 탭이 뒤로 가면 멈추고 돌아오면 곧바로 한 번 부른다. 예전에는 날
 * `setInterval` 이라 아무도 보지 않는 탭에서도 계속 받았다.
 */
export function useTranscodePolling(
  assets: ReadonlyArray<Pick<Asset, 'id' | 'transcode'>>,
  reload: () => void,
  intervalMs: number,
): void {
  const signature = transcodeSignature(assets);
  /*
   * 모양이 마지막으로 바뀐 때. 렌더 중에 고쳐도 되는 순수한 기록이다 — state 로 두면 바뀔 때마다
   * 한 번 더 그린다. 다시 받을 때마다 다시 그려지므로 멈춘 지 10분이 지나면 다음 그림에서 꺼진다.
   */
  const seen = useRef({ signature: '', at: 0 });
  if (seen.current.signature !== signature) seen.current = { signature, at: Date.now() };
  usePolling(reload, intervalMs, shouldPollTranscode(signature, seen.current.at, Date.now()));
}
